import { Badge } from "@nimit9/signet-ui/components/badge"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"
import { Amount } from "@/components/common/amount"
import { useInvestments } from "@/hooks/use-investments"
import { api } from "@/lib/api"
import { cn } from "@/lib/cn"
import { computeTax, NEW_SLABS, newRegimeIncomeTax, slabTax } from "./-compute"
import { betterRegime, buildTaxHeadline } from "./-headline"
import { CARD, CalculatorLayout, InputGroupHeading, NumberField } from "./-layout"

export const Route = createFileRoute("/calculators/tax-regime")({
  component: TaxRegime,
})

const NEW_REGIME_SLABS = [
  { range: "Up to ₹4L", rate: "0%" },
  { range: "₹4L – ₹8L", rate: "5%" },
  { range: "₹8L – ₹12L", rate: "10%" },
  { range: "₹12L – ₹16L", rate: "15%" },
  { range: "₹16L – ₹20L", rate: "20%" },
  { range: "₹20L – ₹24L", rate: "25%" },
  { range: "Above ₹24L", rate: "30%" },
]

function TaxRegime() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const { data: investData } = useInvestments()
  const { data: analytics } = useQuery({
    queryKey: ["analytics", 12],
    queryFn: () => api.get<{ incomeVsExpense: Array<{ income: number }> }>("/analytics?months=12"),
  })
  const [prefilled, setPrefilled] = useState(false)
  const [gross, setGross] = useState(1500000)
  const [c80, setC80] = useState(150000)
  const [c80d, setC80d] = useState(25000)
  const [hra, setHra] = useState(0)
  const [other, setOther] = useState(0)

  // Pre-fill income from analytics — annualize avg monthly income across available months
  const analyticsApplied = useRef(false)
  useEffect(() => {
    if (analyticsApplied.current || !analytics?.incomeVsExpense?.length) return
    const months = analytics.incomeVsExpense.length
    const totalIncome = analytics.incomeVsExpense.reduce((s, m) => s + m.income, 0)
    const annualized = Math.round(((totalIncome / months) * 12) / 10000) * 10000
    if (annualized > 0) {
      analyticsApplied.current = true
      setGross(annualized)
      setPrefilled(true)
    }
  }, [analytics])

  // Pre-fill 80C from investments — independent of analytics
  const investApplied = useRef(false)
  useEffect(() => {
    if (investApplied.current || !investData?.tax?.section80c?.elss) return
    investApplied.current = true
    setC80(Math.min(150000, Math.round(investData.tax.section80c.elss)))
    setPrefilled(true)
  }, [investData])

  const result = useMemo(
    () => computeTax(gross, c80, c80d, hra, other),
    [gross, c80, c80d, hra, other],
  )
  const headline = buildTaxHeadline(result)
  // The regime with the lower tax is the better one.
  const better = betterRegime(result)

  const regimes = [
    {
      key: "old",
      regime: "Old Regime",
      taxable: result.oldTaxable,
      tax: result.oldTax,
      surcharge: result.oldSurcharge,
      note: (
        <>
          Std deduction ₹50K + deductions of <Amount value={result.oldDeductions - 50000} />
        </>
      ),
    },
    {
      key: "new",
      regime: "New Regime",
      taxable: result.newTaxable,
      tax: result.newTax,
      surcharge: result.newSurcharge,
      note:
        result.newTaxable <= 1200000
          ? "Full rebate u/s 87A — zero tax"
          : newRegimeIncomeTax(result.newTaxable) < slabTax(result.newTaxable, NEW_SLABS)
            ? "87A marginal relief — tax capped at your income above ₹12L"
            : "Std deduction ₹75K, no other deductions",
    },
  ] as const

  return (
    <CalculatorLayout
      title="Tax Regime Comparison"
      headline={headline}
      isDesktop={isDesktop}
      prefilledNote={prefilled ? "Pre-filled from your data" : null}
      inputs={
        <>
          <p className="text-caption text-text-muted">FY 2025-26 · Includes 4% cess</p>
          <NumberField
            id="tax-gross"
            label="Gross annual income (₹)"
            value={gross}
            onChange={setGross}
            clamp={(n) => Math.max(0, n)}
          />
          <InputGroupHeading>Old Regime Deductions</InputGroupHeading>
          <NumberField
            id="tax-80c"
            label="Section 80C (max ₹1.5L)"
            value={c80}
            onChange={setC80}
            clamp={(n) => Math.min(150000, Math.max(0, n))}
          />
          <NumberField
            id="tax-80d"
            label="Section 80D — health insurance (max ₹25K)"
            value={c80d}
            onChange={setC80d}
            clamp={(n) => Math.min(25000, Math.max(0, n))}
          />
          <NumberField
            id="tax-hra"
            label="HRA exemption (₹)"
            value={hra}
            onChange={setHra}
            clamp={(n) => Math.max(0, n)}
          />
          <NumberField
            id="tax-other"
            label="Other deductions (NPS, 24b, etc.)"
            value={other}
            onChange={setOther}
            clamp={(n) => Math.max(0, n)}
          />
        </>
      }
    >
      <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
        {regimes.map((r) => {
          const isBetter = better === r.key
          return (
            <section
              key={r.key}
              data-testid={`regime-${r.key}`}
              data-better={isBetter ? "true" : undefined}
              aria-label={r.regime}
              className={cn(
                CARD,
                "flex min-w-0 flex-col gap-1 p-4 md:p-5",
                isBetter && "ring-accent-green/50",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-body font-medium text-text-primary">{r.regime}</h2>
                {isBetter && <Badge>Better</Badge>}
              </div>
              <p className="mt-3 text-caption text-text-muted">Taxable income</p>
              <Amount value={r.taxable} className="text-callout font-medium text-text-primary" />
              <p className="mt-3 text-caption text-text-muted">Tax payable (incl. cess)</p>
              <Amount
                value={r.tax}
                className={cn(
                  "text-title font-semibold tracking-tight",
                  r.tax === 0 ? "text-money-credit" : "text-text-primary",
                )}
              />
              {r.surcharge > 0 && (
                <p data-testid={`surcharge-${r.key}`} className="text-caption text-text-muted">
                  Includes surcharge of <Amount value={r.surcharge} /> (before cess)
                </p>
              )}
              <p data-testid={`note-${r.key}`} className="mt-2 text-caption text-text-muted">
                {r.note}
              </p>
            </section>
          )
        })}
      </div>

      <section aria-labelledby="tax-slabs" className={cn(CARD, "flex flex-col gap-3 p-4 md:p-5")}>
        <h2 id="tax-slabs" className="text-callout font-semibold text-text-primary">
          New regime slabs (FY 2025-26)
        </h2>
        <dl className="flex flex-col gap-1">
          {NEW_REGIME_SLABS.map((s) => (
            <div key={s.range} className="flex justify-between gap-3 text-body">
              <dt className="text-text-secondary">{s.range}</dt>
              <dd className="font-mono font-medium tabular-nums text-text-primary">{s.rate}</dd>
            </div>
          ))}
        </dl>
        <p className="text-caption text-text-muted">
          + 4% health & education cess on tax + surcharge · Rebate u/s 87A up to ₹60K if taxable
          income ≤ ₹12L, with marginal relief just above
        </p>
        <p className="text-caption text-text-muted">
          Surcharge above ₹50L / ₹1Cr / ₹2Cr: 10% / 15% / 25% (old regime 37% above ₹5Cr), with
          marginal relief at each step.
        </p>
      </section>
    </CalculatorLayout>
  )
}
